import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CheckCircleOutline,
  LockReset,
  MailOutline,
} from '@mui/icons-material';
import { DialogTopBar } from '../core/DialogTopBar';
import React, { useEffect, useRef, useState } from 'react';
import { IConfig } from '../../Interfaces/IConfig';
import md5 from 'md5';
import { customFetcher } from '../../hooks/useFetch';
import { getIsEmail } from '../core/generic_helper';
import { useNavigate, useParams } from 'react-router-dom';
import { HelpDialog } from './HelpDialog';
import CountdownSoftLock from './CountdownSoftLock';
import type { LoginAuthResponse, SendAuthFn } from './LoginDriver';

interface IProps {
  msg: string;
  isLoading: boolean;
  setIdContact: React.Dispatch<React.SetStateAction<number>>;
  setTmpSessionToken: React.Dispatch<React.SetStateAction<string>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  sendFunction: SendAuthFn;
  configObject: IConfig;
  enableAutoLogin: boolean;
  softLockTime: null | string;
  setSoftLockTime: React.Dispatch<React.SetStateAction<string | null>>;
}

type AutoLoginParams = Partial<{
  email: string;
  emailProvider: string;
  pw: string;
}>;

const getFromParams = (
  params: AutoLoginParams,
  attr: 'email' | 'pw' | 'emailProvider'
) => {
  if (
    params.email !== undefined &&
    params.emailProvider !== undefined &&
    params.pw !== undefined
  ) {
    if (attr === 'email') {
      return `${params.email}@${params.emailProvider}`;
    } else {
      return params.pw;
    }
  }
  return '';
};

export const LoginMain: React.FC<IProps> = ({
  msg,
  isLoading,
  setIdContact,
  setTmpSessionToken,
  setCurrentPage,
  sendFunction,
  configObject,
  enableAutoLogin,
  softLockTime,
  setSoftLockTime,
}) => {
  const theme = useTheme();
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  useEffect(() => {
    if (isOpenDialog === undefined) {
      setIsOpenDialog(false);
    }
  }, [setIsOpenDialog]);

  const params = useParams();
  const navigation = useNavigate();
  const loginBtnRef = useRef<HTMLButtonElement | null>(null);

  const [email, setEmail] = useState<string>(
    enableAutoLogin ? getFromParams(params, 'email') : ''
  );
  const [pw, setPw] = useState<string>(
    enableAutoLogin ? getFromParams(params, 'pw') : ''
  );

  const [isOpenRequestResetPasswort, setIsOpenRequestResetPasswort] =
    useState(false);
  const [emailToRequest, setEmailToRequest] = useState('');
  const [wasSend, setWasSend] = useState(false);

  const handleOpenRequestNewPw = () => {
    setEmailToRequest('');
    setIsOpenRequestResetPasswort(true);
  };

  const handleAfterSave = (res: LoginAuthResponse) => {
    if (res.detail !== undefined) {
      localStorage.setItem('soft_lock_till', res.detail);
      setSoftLockTime(res.detail);
    } else if (res.TempSessionToken !== undefined) {
      setTmpSessionToken(res.TempSessionToken);
      setIdContact(res.idContact ?? -1);
      setCurrentPage(1);
    } else {
      localStorage.setItem('SessionToken', res.SessionToken ?? '');
      localStorage.setItem(
        'TimestampSessionToken',
        res.TimestampSessionToken ?? ''
      );
      localStorage.setItem('idContact', String(res.idContact ?? ''));
      localStorage.setItem('role', res.Role ?? '');
      localStorage.setItem('scope', res.Scope ?? '');
      localStorage.setItem(
        'passwordResetRequired',
        res.passwordResetRequired ?? 'false'
      );
      window.location.reload();
    }
  };

  const handleSubmitLogin = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const uploadObject: Record<string, string> = {
      Email: email,
      Password: md5(pw),
    };

    const deviceToken = localStorage.getItem('DeviceToken');
    if (deviceToken !== null) {
      uploadObject['DeviceToken'] = deviceToken;
    }

    sendFunction(uploadObject, handleAfterSave, () => {
      // Bei fehlgeschlagener Anmeldung die E-Mail stehen lassen, damit der
      // Mandant sie nur prüfen/korrigieren muss; nur das Passwort für eine
      // saubere erneute Eingabe leeren.
      setPw('');
    });
  };

  const handleSendRequestNewPw = () => {
    if (emailToRequest !== '' && getIsEmail(emailToRequest)) {
      customFetcher({ endpoint: '/contact/requestNewPassword', method: 'POST', body: { Email: emailToRequest } })
        .catch(console.error);
      setWasSend(true);
    }
  };

  useEffect(() => {
    if (
      enableAutoLogin &&
      loginBtnRef !== null &&
      params.email !== undefined &&
      params.emailProvider !== undefined &&
      params.pw !== undefined
    ) {
      navigation('/');
      localStorage.removeItem('SessionToken');
      loginBtnRef.current?.click();
    }
  }, [loginBtnRef, params]);

  return (
    <form onSubmit={handleSubmitLogin} autoComplete='on'>
      <Dialog
        open={isOpenRequestResetPasswort}
        onClose={() => setIsOpenRequestResetPasswort(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: theme => ({
            borderRadius: 3,
            boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.18)}`,
          }),
        }}
      >
        <DialogTopBar
          icon={<LockReset />}
          title='Passwort zurücksetzen'
          subtitle={
            wasSend
              ? 'Bitte prüfen Sie Ihr E-Mail-Postfach'
              : 'Wir senden Ihnen einen Link zum Zurücksetzen zu'
          }
          onClose={() => {
            setIsOpenRequestResetPasswort(false);
            setWasSend(false);
          }}
        />

        {wasSend ? (
          <>
            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Alert
                severity='success'
                icon={<CheckCircleOutline fontSize='small' />}
                sx={{ mt: { xs: 2, sm: 2.5 } }}
              >
                <Typography variant='body2' sx={{ mb: 0.5 }}>
                  <strong>Anfrage erhalten.</strong>
                </Typography>
                <Typography variant='body2'>
                  Wenn ein Konto zu der angegebenen Adresse existiert, erhalten
                  Sie in Kürze eine{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>E-Mail</span> mit einem
                  Link, über den Sie ein neues Passwort vergeben können.
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mt: 1 }}
                >
                  Falls keine E-Mail eintrifft, prüfen Sie bitte den Spam-Ordner
                  oder wenden Sie sich an Ihr Notarbüro.
                </Typography>
              </Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
              <Button
                variant='contained'
                onClick={() => {
                  setIsOpenRequestResetPasswort(false);
                  setWasSend(false);
                }}
              >
                Schließen
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2} sx={{ mt: { xs: 2, sm: 2.5 } }}>
                <Typography variant='body2' color='text.secondary'>
                  Geben Sie die E-Mail-Adresse Ihres Kontos an. Wir senden Ihnen
                  einen Link, mit dem Sie ein neues Passwort vergeben können.
                </Typography>
                <TextField
                  label='E-Mail-Adresse'
                  value={emailToRequest}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setEmailToRequest(event.target.value)
                  }
                  error={emailToRequest !== '' && !getIsEmail(emailToRequest)}
                  helperText={
                    emailToRequest !== '' && !getIsEmail(emailToRequest)
                      ? 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
                      : ' '
                  }
                  placeholder='ihre.adresse@beispiel.de'
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <MailOutline
                        sx={{
                          fontSize: 18,
                          color: 'text.secondary',
                          mr: 1,
                        }}
                      />
                    ),
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
              <Button onClick={() => setIsOpenRequestResetPasswort(false)}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                disabled={emailToRequest === '' || !getIsEmail(emailToRequest)}
                onClick={handleSendRequestNewPw}
              >
                Link anfordern
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 4, md: 6 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 1.5, md: 5 }}
          sx={{ maxWidth: 1180, alignItems: 'stretch' }}
        >
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              order: { xs: 2, md: 1 },
              display: { xs: 'none', md: 'block' },
            }}
          >
            <Box
              sx={{
                height: '100%',
                borderRadius: 6,
                p: 5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha('#ffffff', 0.88)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              }}
            >
              <Box>
                <Box
                  component='img'
                  alt={`${configObject.company_name} Logo`}
                  src={`data:${configObject.logo_mime};base64,${configObject.logo}`}
                  sx={{
                    width: 'auto',
                    maxWidth: 340,
                    maxHeight: 130,
                    objectFit: 'contain',
                    mb: 4,
                  }}
                />
                <Typography variant='h3' sx={{ mb: 2 }}>
                  Ihr direkter Draht zum Notarbüro.
                </Typography>
                <Typography variant='body1' sx={{ maxWidth: 520, color: 'text.secondary' }}>
                  Verfolgen Sie den Fortschritt Ihrer Vorgänge, tauschen Sie
                  Dokumente sicher aus und behalten Sie alle Termine und
                  Nachrichten an einem Ort im Blick.
                </Typography>
              </Box>

              <Stack spacing={1.5} sx={{ mt: { xs: 2.5, md: 4 } }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Was Sie hier erledigen können:
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Den aktuellen Stand Ihrer Vorgänge einsehen
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Dokumente sicher hochladen und herunterladen
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Einladungen beantworten und Stammdaten pflegen
                </Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CardContent sx={{ width: '100%', p: { xs: 3, md: 5 } }}>
                <Box
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    justifyContent: 'center',
                    mb: 4,
                  }}
                >
                  <Box
                    component='img'
                    alt={`${configObject.company_name} Logo`}
                    src={`data:${configObject.logo_mime};base64,${configObject.logo}`}
                    sx={{
                      width: 'auto',
                      maxWidth: 320,
                      maxHeight: 130,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Typography variant='h4' sx={{ mb: 1 }}>
                  Anmeldung
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 4 }}>
                  Bitte melden Sie sich mit Ihren Zugangsdaten an.
                </Typography>

                <Stack spacing={2.25}>
                  <TextField
                    label='E-Mail'
                    value={email}
                    fullWidth
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(String(event.target.value))
                    }
                    required
                  />

                  <TextField
                    type='password'
                    label='Passwort'
                    fullWidth
                    value={pw}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setPw(String(event.target.value))
                    }
                    required
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                      pt: 1,
                    }}
                  >
                    <Button onClick={() => setIsOpenDialog(true)} size='small'>
                      Hilfe
                    </Button>
                    <Button onClick={handleOpenRequestNewPw} size='small'>
                      Passwort zurücksetzen
                    </Button>
                  </Box>

                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    ref={loginBtnRef}
                    fullWidth
                    disabled={isLoading}
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={18} color='inherit' />
                      ) : undefined
                    }
                    sx={{ mt: 1 }}
                  >
                    {/* Eigenes span: Eine Browser-Übersetzung ersetzt lose
                        Textknoten, dann scheitert das Einfügen des Lade-Symbols
                        davor (#1337). */}
                    <span>{isLoading ? 'Anmeldung läuft …' : 'Anmelden'}</span>
                  </Button>

                  <Box sx={{ minHeight: 68 }}>
                    {softLockTime !== null &&
                    new Date(String(softLockTime)).getTime() > Date.now() ? (
                      <CountdownSoftLock
                        key={`softlock-${softLockTime}`}
                        targetTime={new Date(String(softLockTime))}
                      />
                    ) : (
                      msg !== '' && (
                        <Alert severity='error'>
                          {msg}
                          <Box sx={{ mt: 0.5 }}>
                            Bitte prüfen Sie E-Mail und Passwort — oder{' '}
                            <Link
                              component='button'
                              type='button'
                              onClick={handleOpenRequestNewPw}
                              sx={{ verticalAlign: 'baseline' }}
                            >
                              setzen Sie Ihr Passwort zurück
                            </Link>
                            .
                          </Box>
                        </Alert>
                      )
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <HelpDialog
          isOpen={isOpenDialog}
          onClose={() => setIsOpenDialog(false)}
        />
      </Box>
    </form>
  );
};
